/**
 * Motor de Apariencia, Colores, Tipografía y Configuración del Cliente (Tenant).
 *
 * Resuelve dos problemas críticos:
 * 1. Unifica la paleta de colores y contraste (WCAG) en todas las vistas del
 *    sistema para que ningún texto quede claro sobre fondo claro ni oscuro
 *    sobre fondo oscuro.
 * 2. Permite que cada negocio personalice sus colores, modo visual (claro /
 *    oscuro), familia y escala tipográfica, diseño de ticket térmico y datos
 *    comerciales desde la sección Configuración.
 */

export type ThemeMode = "light" | "dark";
export type FontFamilyKey = "inter" | "manrope" | "poppins" | "roboto" | "jetbrains";
export type FontScaleKey = "compact" | "normal" | "large";
export type TicketPaperWidth = "80mm" | "58mm";

export interface TenantSettings {
  // --- Apariencia, Colores y Fuente ---
  primary_color: string;
  secondary_color: string;
  theme_mode: ThemeMode;
  font_family: FontFamilyKey;
  font_scale: FontScaleKey;
  border_radius: "sharp" | "rounded" | "pill";

  // --- Identidad Comercial y Contacto ---
  commercial_slogan: string;
  logo_url: string;
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  website_url: string;

  // --- Diseño y Personalización de Ticket ---
  ticket_paper_width: TicketPaperWidth;
  ticket_show_logo: boolean;
  ticket_show_slogan: boolean;
  ticket_show_rfc: boolean;
  ticket_show_regime: boolean;
  ticket_show_address: boolean;
  ticket_show_phone: boolean;
  ticket_show_cashier: boolean;
  ticket_show_branch: boolean;
  ticket_show_folio: boolean;
  ticket_show_tax_breakdown: boolean;
  ticket_header_note: string;
  ticket_footer_note: string;
  ticket_billing_instructions: string;
  ticket_auto_print: boolean;

  // --- Operación ---
  default_sale_series: string;
  low_stock_threshold: number;
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = {
  primary_color: "#007BFF",
  secondary_color: "#001F3F",
  theme_mode: "light",
  font_family: "inter",
  font_scale: "normal",
  border_radius: "rounded",

  commercial_slogan: "Calidad y servicio en cada venta",
  logo_url: "",
  contact_phone: "",
  contact_email: "",
  contact_address: "",
  website_url: "",

  ticket_paper_width: "80mm",
  ticket_show_logo: true,
  ticket_show_slogan: true,
  ticket_show_rfc: true,
  ticket_show_regime: true,
  ticket_show_address: true,
  ticket_show_phone: true,
  ticket_show_cashier: true,
  ticket_show_branch: true,
  ticket_show_folio: true,
  ticket_show_tax_breakdown: true,
  ticket_header_note: "¡Bienvenido!",
  ticket_footer_note: "¡Gracias por su preferencia! Conserve este ticket para cualquier aclaración.",
  ticket_billing_instructions: "Solicite su factura dentro del mes en curso.",
  ticket_auto_print: false,

  default_sale_series: "A",
  low_stock_threshold: 5,
};

export interface ColorPreset {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  description: string;
}

export const COLOR_PRESETS: ColorPreset[] = [
  {
    id: "azul-megasyr",
    name: "Azul Eléctrico MegaSyR",
    primary: "#007BFF",
    secondary: "#001F3F",
    description: "Paleta oficial MegaSyR de alto contraste",
  },
  {
    id: "terracota",
    name: "Terracota Gastronómico",
    primary: "#D4622B",
    secondary: "#1B1916",
    description: "Cálido, ideal para restaurantes y cafeterías",
  },
  {
    id: "esmeralda",
    name: "Esmeralda Fresco",
    primary: "#059669",
    secondary: "#064E3B",
    description: "Ideal para farmacias, abarrotes y mercados",
  },
  {
    id: "violeta",
    name: "Violeta Moderno",
    primary: "#7C3AED",
    secondary: "#1E1B4B",
    description: "Ideal para boutiques, estéticas y servicios",
  },
  {
    id: "rojo-intenso",
    name: "Rojo Intenso",
    primary: "#DC2626",
    secondary: "#18181B",
    description: "Ideal para comida rápida, taquerías y refaccionarias",
  },
  {
    id: "turquesa",
    name: "Turquesa Ejecutivo",
    primary: "#0D9488",
    secondary: "#134E4A",
    description: "Limpio, clínico y profesional",
  },
  {
    id: "ambar",
    name: "Ámbar Artesanal",
    primary: "#B45309",
    secondary: "#1C1917",
    description: "Ideal para panaderías, bares y talleres",
  },
  {
    id: "grafito",
    name: "Grafito Minimal",
    primary: "#334155",
    secondary: "#0F172A",
    description: "Sobrio, neutro y ejecutivo",
  },
];

export const FONT_FAMILY_MAP: Record<FontFamilyKey, { label: string; css: string; sample: string }> = {
  inter: {
    label: "Inter (Estándar POS)",
    css: '"Inter", system-ui, -apple-system, sans-serif',
    sample: "Lectura ultra clara para terminales táctiles",
  },
  manrope: {
    label: "Manrope (Geométrica Moderna)",
    css: '"Manrope", "Inter", system-ui, sans-serif',
    sample: "Números amplios y estética moderna",
  },
  poppins: {
    label: "Poppins (Amigable y Redondeada)",
    css: '"Poppins", "Inter", system-ui, sans-serif',
    sample: "Ideal para restaurantes y cafeterías",
  },
  roboto: {
    label: "Roboto (Clásica Industrial)",
    css: '"Roboto", "Inter", system-ui, sans-serif',
    sample: "Compacta y familiar en cualquier dispositivo",
  },
  jetbrains: {
    label: "JetBrains Mono (Técnica / Taller)",
    css: '"JetBrains Mono", ui-monospace, monospace',
    sample: "Precisión numérica y códigos SKU alineados",
  },
};

export const FONT_SCALE_MAP: Record<FontScaleKey, { label: string; rootFontSize: string }> = {
  compact: { label: "Compacta (Más datos en pantalla)", rootFontSize: "14.5px" },
  normal: { label: "Normal (Recomendada)", rootFontSize: "16px" },
  large: { label: "Grande (Pantallas táctiles / Mayor legibilidad)", rootFontSize: "17.5px" },
};

/** Convierte `#RRGGBB` a `{ r, g, b }`. */
export function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

/** Oscurece un color HEX en un porcentaje (0..1) para estados `:hover` / `pressed`. */
export function darkenHex(hex: string, amount = 0.18): string {
  const parsed = parseHexColor(hex);
  if (!parsed) return "#0056B3";
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(parsed.r * (1 - amount));
  const g = clamp(parsed.g * (1 - amount));
  const b = clamp(parsed.b * (1 - amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Aclara un color HEX mezclándolo con blanco (0..1). */
export function tintHex(hex: string, whiteRatio = 0.35): string {
  const parsed = parseHexColor(hex);
  if (!parsed) return "#60A5FA";
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(parsed.r * (1 - whiteRatio) + 255 * whiteRatio);
  const g = clamp(parsed.g * (1 - whiteRatio) + 255 * whiteRatio);
  const b = clamp(parsed.b * (1 - whiteRatio) + 255 * whiteRatio);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Guardián de contraste WCAG 2.1:
 * Calcula la luminancia relativa del color de fondo y devuelve `#FFFFFF` (claro)
 * o `#1B2127` (oscuro) para garantizar que el texto de los botones siempre sea
 * perfectamente legible.
 */
export function getContrastForeground(hex: string): "#FFFFFF" | "#1B2127" {
  const parsed = parseHexColor(hex);
  if (!parsed) return "#FFFFFF";
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * toLinear(parsed.r) + 0.7152 * toLinear(parsed.g) + 0.0722 * toLinear(parsed.b);
  return lum > 0.42 ? "#1B2127" : "#FFFFFF";
}

const THEME_STORAGE_PREFIX = "megasyr.theme.";

export function mergeTenantSettings(raw?: Record<string, unknown> | null): TenantSettings {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_TENANT_SETTINGS };
  }
  return {
    ...DEFAULT_TENANT_SETTINGS,
    ...(raw as Partial<TenantSettings>),
  };
}

export function loadCachedTenantSettings(tenantSlug?: string | null): TenantSettings {
  try {
    const key = tenantSlug ? `${THEME_STORAGE_PREFIX}${tenantSlug}` : `${THEME_STORAGE_PREFIX}current`;
    const stored = localStorage.getItem(key);
    if (!stored) return { ...DEFAULT_TENANT_SETTINGS };
    return mergeTenantSettings(JSON.parse(stored) as Record<string, unknown>);
  } catch {
    return { ...DEFAULT_TENANT_SETTINGS };
  }
}

export function getEffectiveTenantSettings(
  tenant?: { slug?: string; settings?: Record<string, unknown> | null } | null,
): TenantSettings {
  const cached = loadCachedTenantSettings(tenant?.slug);
  if (tenant?.settings && Object.keys(tenant.settings).length > 0) {
    return {
      ...DEFAULT_TENANT_SETTINGS,
      ...cached,
      ...(tenant.settings as Partial<TenantSettings>),
    };
  }
  return cached;
}

/**
 * Aplica en vivo los tokens CSS del negocio al `document.documentElement` y
 * guarda una copia en `localStorage` para que al recargar no haya parpadeo.
 */
export function applyTenantTheme(
  partialSettings?: Record<string, unknown> | Partial<TenantSettings> | null,
  tenantSlug?: string | null,
): TenantSettings {
  const settings = mergeTenantSettings(partialSettings as Record<string, unknown> | null);

  if (typeof document !== "undefined") {
    const root = document.documentElement;
    const primaryHex = parseHexColor(settings.primary_color)
      ? settings.primary_color
      : DEFAULT_TENANT_SETTINGS.primary_color;
    const hoverHex = darkenHex(primaryHex, 0.24);
    const pressedHex = darkenHex(primaryHex, 0.14);
    const onElectricHex = getContrastForeground(primaryHex);
    const electricTextHex =
      settings.theme_mode === "dark" ? tintHex(primaryHex, 0.35) : darkenHex(primaryHex, 0.2);

    root.style.setProperty("--color-electric", primaryHex);
    root.style.setProperty("--color-electric-hover", hoverHex);
    root.style.setProperty("--color-electric-pressed", pressedHex);
    root.style.setProperty("--color-electric-text", electricTextHex);
    root.style.setProperty("--color-on-electric", onElectricHex);

    // Modo de superficie (claro u oscuro) con alto contraste garantizado
    if (settings.theme_mode === "dark") {
      root.dataset.theme = "dark";
      root.style.setProperty("--color-surface", "#13181d");
      root.style.setProperty("--color-snow", "#1d242b");
      root.style.setProperty("--color-carbon", "#f4f6f8");
      root.style.setProperty("--color-line", "#2e3944");
      root.style.setProperty("--color-muted", "#9aa8b6");
    } else {
      root.dataset.theme = "light";
      root.style.setProperty("--color-surface", "#f5f7f8");
      root.style.setProperty("--color-snow", "#ffffff");
      root.style.setProperty("--color-carbon", "#1b2127");
      root.style.setProperty("--color-line", "#dce2e6");
      root.style.setProperty("--color-muted", "#52606b");
    }

    // Familia tipográfica
    const fontConfig = FONT_FAMILY_MAP[settings.font_family] ?? FONT_FAMILY_MAP.inter;
    root.style.setProperty("--font-sans", fontConfig.css);

    // Escala tipográfica
    const scaleConfig = FONT_SCALE_MAP[settings.font_scale] ?? FONT_SCALE_MAP.normal;
    root.style.fontSize = scaleConfig.rootFontSize;
  }

  try {
    if (tenantSlug) {
      localStorage.setItem(`${THEME_STORAGE_PREFIX}${tenantSlug}`, JSON.stringify(settings));
    }
    localStorage.setItem(`${THEME_STORAGE_PREFIX}current`, JSON.stringify(settings));
  } catch {
    // Ignorar errores de almacenamiento local
  }

  return settings;
}
